import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Check, Clock, Copy, CreditCard, MessageCircle, Pencil, Send, StickyNote, Trash2, Undo2, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api';
import { cn } from '../../lib/utils';

export const KIND = {
  tache: { label: 'Tâche', icon: '✅', cls: 'bg-[#0288D1]/10 text-[#0288D1]', done: 'Tâche terminée' },
  paiement: { label: 'Paiement', icon: '💳', cls: 'bg-amber-100 text-amber-700', done: 'Payé' },
  idee: { label: 'Idée', icon: '💡', cls: 'bg-purple-100 text-purple-700', done: 'Noté, c\'est fait' },
  rappel: { label: 'Rappel', icon: '🔔', cls: 'bg-[#26A69A]/10 text-[#26A69A]', done: 'C\'est fait' },
  message: { label: 'Message', icon: '💬', cls: 'bg-pink-100 text-pink-700', done: 'Envoyé' },
};
const DONE_LABEL = { done: 'Fait', noreply: 'Pas besoin de répondre', wait: 'Mis à plus tard', fix: 'Changement demandé',
  send: 'Confié à Claude pour l\'envoi' };
const LATER = [['ce_soir', 'Ce soir (18h)'], ['demain', 'Demain'], ['semaine', 'Semaine prochaine']];

const fmtDay = (d) => { try { return format(new Date(d), 'd MMM yyyy', { locale: fr }); } catch { return d; } };
const fmtAt = (d) => { try { return format(new Date(d), 'd MMM', { locale: fr }); } catch { return ''; } };
const firstUrl = (card) => card.steps?.find(s => s.url)?.url;

const Btn = ({ primary, className, children, ...p }) => (
  <button type="button" {...p} className={cn('rounded-xl font-semibold py-2.5 px-2 flex items-center justify-center gap-1.5 text-sm disabled:opacity-40',
    primary ? 'bg-[#26A69A] hover:bg-[#219e92] text-white' : 'border border-slate-200 text-slate-700 bg-white', className)}>{children}</button>
);

function Panel({ title, children, onClose }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-2">
      <div className="flex items-center text-sm font-bold text-slate-700">{title}
        <button type="button" onClick={onClose} className="ml-auto text-slate-400" aria-label="Fermer"><X size={16} /></button></div>
      {children}
    </div>
  );
}

function TextBox({ placeholder, button, onSubmit, busy, initial = '' }) {
  const [t, setT] = useState(initial);
  return (
    <>
      <textarea value={t} onChange={e => setT(e.target.value)} rows={3} autoFocus placeholder={placeholder}
        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-base bg-white" />
      <button type="button" disabled={!t.trim() || busy} onClick={() => onSubmit(t.trim())}
        className="w-full rounded-xl bg-[#0288D1] text-white font-semibold py-2 disabled:opacity-40">{button}</button>
    </>
  );
}

/**
 * Une carte du cockpit. Le type décide des boutons :
 *  💬 message : ce que la personne a écrit, ma réponse proposée (modifiable), Envoyé / Claude l'envoie / Pas besoin…
 *  ✅ tâche · 💳 paiement : étapes à cocher, un seul bouton « terminé » pour toute la carte.
 * `previous` = carte déjà traitée revue avec « ← Précédente » (seulement Annuler / Retour).
 */
export default function NowCard({ card, busy, previous, onUndo, onBack, onDone, onNoReply, onWait, onFix, onNote, onStep,
  onDelete, onDraft, onSend }) {
  const [panel, setPanel] = useState(null);  // later | note | fix | send | delete
  const [draft, setDraft] = useState(card.draft || '');
  useEffect(() => { setPanel(null); setDraft(card.draft || ''); }, [card.id, card.draft]);

  const kind = KIND[card.kind] || KIND.tache;
  const isMsg = card.kind === 'message';
  const url = firstUrl(card);
  const notes = [...(card.notes || [])].reverse();
  const saveDraft = () => { if (draft.trim() && draft.trim() !== (card.draft || '').trim()) onDraft(draft.trim()); };
  const copyAndOpen = async () => {
    try { await navigator.clipboard.writeText(draft); toast.success('Réponse copiée'); } catch { toast.error('Copie impossible : sélectionne le texte'); }
    if (url) window.open(url, '_blank', 'noopener');
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 lg:p-5 space-y-3" data-testid="now-card" data-kind={card.kind}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={cn('font-bold px-2 py-0.5 rounded-full', kind.cls)}>{kind.icon} {kind.label}</span>
        {isMsg && card.channel && <span className="font-semibold text-slate-500">{card.channel}{card.received_at ? ` · ${fmtDay(card.received_at)}` : ''}</span>}
        <span className="text-slate-400">{previous ? `Précédente · ${DONE_LABEL[previous] || ''}` : `~${card.duration_min || 5} min`}</span>
      </div>
      <h2 className="text-xl font-extrabold text-slate-900 font-manrope leading-tight" data-testid="now-title">{card.title}</h2>
      {card.why && <div className="bg-amber-50 text-amber-900 rounded-xl px-3 py-2 text-sm">Pourquoi maintenant : {card.why}</div>}

      {notes.length > 0 && (
        <div className="rounded-xl border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm space-y-1" data-testid="card-notes">
          <div className="font-bold text-yellow-800 text-xs">📝 Tes notes</div>
          {notes.map((n, i) => <div key={i}><span className="text-yellow-700 text-xs mr-1.5">{fmtAt(n.at)}</span>{n.text}</div>)}
        </div>
      )}
      {card.google_notes && <p className="text-xs text-slate-500 whitespace-pre-line">Notes Google : {card.google_notes}</p>}

      {isMsg ? (
        <div className="space-y-2">
          {card.received && (
            <div className="rounded-xl bg-slate-100 px-3 py-2 text-sm">
              <div className="text-xs font-bold text-slate-500 mb-0.5"><MessageCircle size={12} className="inline mr-1" />{card.contact || 'La personne'} a écrit :</div>
              <div className="text-slate-800 whitespace-pre-line">« {card.received} »</div>
            </div>
          )}
          <div className="text-xs font-bold text-slate-500">Ma réponse proposée (tu peux la modifier) :</div>
          <textarea value={draft} onChange={e => setDraft(e.target.value)} onBlur={previous ? undefined : saveDraft} rows={4}
            readOnly={!!previous} data-testid="draft"
            className="w-full rounded-xl border-2 border-[#0288D1]/30 px-3 py-2 text-base bg-white" />
          {!previous && (
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <Btn onClick={copyAndOpen} data-testid="btn-copy-open"><Copy size={15} /> Copier la réponse{url ? ' et ouvrir' : ''}</Btn>
              <Btn onClick={() => setPanel('template')} data-testid="btn-template">💬 Message type</Btn>
            </div>
          )}
        </div>
      ) : card.steps?.length > 0 && (
        <ul className="space-y-1.5">
          {card.steps.map((s, i) => (
            <li key={i}>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!s.done} disabled={!!previous} onChange={() => onStep(i)} className="mt-1 w-4 h-4 accent-[#26A69A]" />
                <span className={cn(s.done && 'line-through text-slate-400')}>
                  {s.text.replace(/^[A-Z]\.\s+/, '')}{s.url && <> — <a href={s.url} target="_blank" rel="noreferrer" className="text-[#0288D1] underline">ouvrir</a></>}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}

      {card.pending_fix && <p className="text-xs text-slate-500">Changement demandé : « {card.pending_fix.text} » — je le fais à mon prochain passage.</p>}
      {card.sync_error && <p className="text-xs text-slate-400">Google Tasks pas encore à jour, je réessaie au prochain passage.</p>}
      {card.send?.state === 'echec' && <p className="text-sm text-red-600" data-testid="send-error">⚠️ L'envoi n'a pas marché : {card.send.error}. Renvoie-le toi-même ou réessaie.</p>}

      {previous ? (
        <div className="grid grid-cols-2 gap-2">
          <Btn onClick={onUndo} disabled={busy} data-testid="btn-undo" className="bg-[#0288D1] text-white border-0"><Undo2 size={16} /> Annuler</Btn>
          <Btn onClick={onBack} data-testid="btn-back">Retour à la carte actuelle</Btn>
        </div>
      ) : (
        <div className="space-y-2">
          {isMsg ? (
            <div className="grid grid-cols-2 gap-2">
              <Btn primary onClick={onDone} disabled={busy} data-testid="btn-done"><Check size={16} /> Je l'ai envoyé</Btn>
              <Btn onClick={() => { saveDraft(); setPanel('send'); }} disabled={busy || !draft.trim()} data-testid="btn-send"
                className="border-[#0288D1] text-[#0288D1]"><Send size={15} /> Claude l'envoie</Btn>
            </div>
          ) : (
            <Btn primary onClick={onDone} disabled={busy} className="w-full" data-testid="btn-done">
              {card.kind === 'paiement' ? <CreditCard size={16} /> : <Check size={16} />} {kind.done}</Btn>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {isMsg && <Btn onClick={onNoReply} disabled={busy} data-testid="btn-noreply">🚫 Pas besoin de répondre</Btn>}
            <Btn onClick={() => setPanel('later')} disabled={busy} data-testid="btn-wait"><Clock size={15} /> Plus tard</Btn>
            <Btn onClick={() => setPanel('note')} disabled={busy} data-testid="btn-note"><StickyNote size={15} /> Note</Btn>
            <Btn onClick={() => setPanel('fix')} disabled={busy} data-testid="btn-fix"><Pencil size={15} /> {isMsg ? 'Change la réponse' : 'Demander un changement'}</Btn>
            {!isMsg && <Btn onClick={() => setPanel('delete')} disabled={busy} data-testid="btn-delete" className="text-red-600"><Trash2 size={15} /> Supprimer</Btn>}
          </div>
        </div>
      )}

      {panel === 'later' && (
        <Panel title="⏰ Je la remets quand ?" onClose={() => setPanel(null)}>
          <LaterBox busy={busy} onPick={(when, note) => onWait(when, note)} />
        </Panel>
      )}
      {panel === 'template' && (
        <Panel title="💬 Insérer un message type" onClose={() => setPanel(null)}>
          <TemplatePicker onPick={(text) => { setDraft(d => (d.trim() ? `${d.trim()}\n\n${text}` : text)); setPanel(null); }} />
        </Panel>
      )}
      {panel === 'note' && (
        <Panel title="📝 Ajouter une note" onClose={() => setPanel(null)}>
          <TextBox busy={busy} placeholder="« attendre le devis de Paul », « demander l'IBAN »… (le micro du clavier marche aussi)"
            button={card.source === 'google_tasks' ? 'Ajouter (aussi dans Google Tasks)' : 'Ajouter la note'}
            onSubmit={(t) => { onNote(t); setPanel(null); }} />
        </Panel>
      )}
      {panel === 'fix' && (
        <Panel title={isMsg ? '✏️ Comment changer la réponse ?' : '✏️ Qu\'est-ce que je change ?'} onClose={() => setPanel(null)}>
          <TextBox busy={busy} button="Envoyer à Claude"
            placeholder={isMsg ? '« plus court », « propose jeudi », « en anglais »… Je refais le brouillon à mon prochain passage.'
              : '« décale à jeudi », « coupe plus petit », « plus utile »…'}
            onSubmit={onFix} />
        </Panel>
      )}
      {panel === 'send' && (
        <Panel title="📤 Je l'envoie pour toi ?" onClose={() => setPanel(null)}>
          <p className="text-sm text-slate-600">À <b>{card.contact || '—'}</b> sur <b>{card.channel || '—'}</b>, exactement ce texte :</p>
          <div className="rounded-xl bg-white border border-slate-200 px-3 py-2 text-sm whitespace-pre-line" data-testid="send-preview">{draft}</div>
          <p className="text-xs text-slate-500">Il part depuis ton Mac dans les 2 à 5 minutes (Mac allumé). Tu peux encore annuler avec « ← Précédente » tant qu'il n'est pas parti.</p>
          <Btn primary disabled={busy || !draft.trim()} onClick={() => onSend(draft.trim())} className="w-full" data-testid="btn-confirm-send">✅ Confirmer l'envoi</Btn>
        </Panel>
      )}
      {panel === 'delete' && (
        <Panel title="🗑 Supprimer cette carte ?" onClose={() => setPanel(null)}>
          <p className="text-sm text-slate-600">{card.source === 'google_tasks'
            ? 'La tâche sera aussi supprimée de Google Tasks. Impossible d\'annuler.' : 'Impossible d\'annuler.'}</p>
          <div className="grid grid-cols-2 gap-2">
            <Btn onClick={onDelete} disabled={busy} className="bg-red-600 text-white border-0" data-testid="btn-confirm-delete">Supprimer</Btn>
            <Btn onClick={() => setPanel(null)}>Non</Btn>
          </div>
        </Panel>
      )}
    </div>
  );
}

function LaterBox({ onPick, busy }) {
  const [note, setNote] = useState('');
  return (
    <>
      <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
        placeholder="Une note pour la prochaine fois ? (facultatif : ce qu'il manque, ce que tu attends…)"
        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-base bg-white" data-testid="later-note" />
      <div className="grid grid-cols-3 gap-2">
        {LATER.map(([when, label]) => (
          <Btn key={when} disabled={busy} onClick={() => onPick(when, note.trim() || null)} data-testid={`later-${when}`}>{label}</Btn>
        ))}
      </div>
    </>
  );
}

function TemplatePicker({ onPick }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get('/memo/messages-types').then(setItems).catch(() => setItems([])); }, []);
  if (items === null) return <p className="text-sm text-slate-400">…</p>;
  if (!items.length) return <p className="text-sm text-slate-500">Aucun message type pour l'instant (menu « Messages types »).</p>;
  return (
    <div className="space-y-1.5 max-h-72 overflow-y-auto">
      {items.map(m => (
        <div key={m.id} className="flex items-center gap-2 bg-white rounded-xl border border-slate-200 px-3 py-2">
          <span className="flex-1 text-sm font-semibold text-slate-700">{m.title}</span>
          <button type="button" onClick={() => onPick(m.text)} className="text-sm font-bold text-[#0288D1]" data-testid="template-fr">FR</button>
          {m.text_en && <button type="button" onClick={() => onPick(m.text_en)} className="text-sm font-bold text-[#0288D1]">EN</button>}
        </div>
      ))}
    </div>
  );
}
